import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import Layout from './components/layout/Layout';
import ErrorBoundary from './components/ErrorBoundary';


const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Programs = lazy(() => import('./pages/Programs'));
const Registration = lazy(() => import('./pages/Registration'));
const PaymentConfirmation = lazy(() => import('./pages/PaymentConfirmation'));
const Contact = lazy(() => import('./pages/Contact'));
const Donate = lazy(() => import('./pages/Donate'));
const Sponsors = lazy(() => import('./pages/Sponsors'));
const SponsorDetail = lazy(() => import('./pages/SponsorDetail'));
const Coaches = lazy(() => import('./pages/Coaches'));
const Camps = lazy(() => import('./pages/Camps'));
const Events = lazy(() => import('./pages/Events'));
const Races = lazy(() => import('./pages/Races'));
const Athletes = lazy(() => import('./pages/Athletes'));
const AthleteProfile = lazy(() => import('./pages/AthleteProfile'));
const Alumni = lazy(() => import('./pages/Alumni'));
const UniversalProgramPage = lazy(() => import('./pages/UniversalProgramPage'));
const PointsGuide = lazy(() => import('./pages/PointsGuide'));
const Scholarship = lazy(() => import('./pages/Scholarship'));
const Scholarships = lazy(() => import('./pages/Scholarships'));
const Fitness = lazy(() => import('./pages/Fitness'));
const Equipment = lazy(() => import('./pages/Equipment'));
const SafeSport = lazy(() => import('./pages/SafeSport'));
const Transportation = lazy(() => import('./pages/Transportation'));
const Lodging = lazy(() => import('./pages/Lodging'));
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const KnownIssues = lazy(() => import('./pages/KnownIssues'));
const GuardianCommunication = lazy(() => import('./pages/GuardianCommunication'));
const DatabaseDebug = lazy(() => import('./pages/DatabaseDebug'));
const AdminGuide = lazy(() => import('./pages/AdminGuide'));
const NotFound = lazy(() => import('./pages/NotFound'));
const AuthActionHandler = lazy(() => import('./components/auth/AuthActionHandler'));
const Unsubscribe = lazy(() => import('./pages/Unsubscribe'));
const CalendarOptOut = lazy(() => import('./pages/CalendarOptOut'));

import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import SessionTimeoutHandler from './components/auth/SessionTimeoutHandler';

import { addPushListener, setupPushNotifications } from './services/pushNotifications';
import { updateAnalyticsConsent } from './services/analyticsService';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from './firebase/config';
import { applyAuthPersistence } from './firebase/authUtils';

function App() {
    const navigate = useNavigate();
    const [user] = useAuthState(auth);

    useEffect(() => {
        addPushListener();
        updateAnalyticsConsent(!!user);
        if (user) {
            setupPushNotifications(user.uid);
            try {
                const params = new URLSearchParams(window.location.search);
                const paramKeep = params.get('keepLoggedIn');
                const pendingKeep = window.localStorage.getItem('pendingKeepLoggedIn');
                if (paramKeep === 'true' || pendingKeep === 'true') {
                    applyAuthPersistence(true);
                }
            } catch { /* ignore */ }
        }
    }, [user, navigate]);

    return (
        <ErrorBoundary>
            <SessionTimeoutHandler user={user} />
            <Layout>
                <Suspense fallback={
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                        <CircularProgress aria-label="Loading page..." />
                    </Box>
                }>
                    <Routes>
                        {/* Main pages */}
                        <Route path="/" element={<Home />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/programs" element={<Programs />} />
                        <Route path="/registration" element={<Registration />} />
                        <Route
                            path="/registration/confirmation"
                            element={
                                <ProtectedRoute allowGuest>
                                    <PaymentConfirmation />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/away-races" element={<Navigate to="/races" replace />} />
                        <Route path="/contact" element={<Contact />} />

                        <Route path="/donate" element={<Donate />} />
                        <Route path="/sponsors" element={<Sponsors />} />
                        <Route path="/sponsorship" element={<Navigate to="/sponsors" replace />} />
                        <Route path="/sponsors/:slug" element={<SponsorDetail />} />
                        <Route path="/sponsorship/:slug" element={<SponsorDetail />} />
                        <Route path="/staff" element={<Coaches />} />
                        <Route path="/camps" element={<Camps />} />
                        <Route path="/events" element={<Events />} />
                        <Route path="/races" element={<Races />} />
                        <Route
                            path="/athletes"
                            element={
                                <ProtectedRoute requireCommunity>
                                    <Athletes />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/athletes/:id"
                            element={
                                <ProtectedRoute requireCommunity>
                                    <AthleteProfile />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/alumni"
                            element={
                                <ProtectedRoute requireCommunity>
                                    <Alumni />
                                </ProtectedRoute>
                            }
                        />

                        {/* Universal Dynamic Program Route */}
                        <Route path="/programs/:slug" element={<UniversalProgramPage />} />
                        <Route path="/camps/:slug" element={<UniversalProgramPage />} />
                        <Route path="/events/:slug" element={<UniversalProgramPage />} />

                        {/* Race routes */}
                        <Route path="/races/:slug" element={<UniversalProgramPage />} />
                        <Route path="/points" element={<PointsGuide />} />
                        <Route path="/usss-points" element={<Navigate to="/points" replace />} />

                        {/* Additional pages */}
                        <Route path="/fitness" element={<Fitness />} />
                        <Route path="/scholarship" element={<Scholarship />} />
                        <Route path="/scholarships" element={<Scholarships />} />
                        <Route path="/equipment-recommendations" element={<Equipment />} />
                        <Route path="/safesport" element={<SafeSport />} />
                        <Route path="/transportation" element={<Transportation />} />
                        <Route path="/lodging" element={<Lodging />} />
                        <Route path="/privacy" element={<Privacy />} />
                        <Route path="/terms" element={<Terms />} />
                        <Route path="/known-issues" element={<KnownIssues />} />
                        <Route path="/communication" element={<GuardianCommunication />} />
                        <Route path="/calendar/opt-out" element={<CalendarOptOut />} />
                        <Route path="/calendar" element={<Navigate to="/dashboard/schedule" replace />} />

                        {/* Authentication */}
                        <Route path="/login" element={<Login />} />
                        <Route path="/auth/action" element={<AuthActionHandler />} />
                        <Route path="/unsubscribe" element={<Unsubscribe />} />
                        {/* Payments redirect */}
                        <Route path="/payments" element={<Navigate to="/dashboard/payments" replace />} />
                        <Route
                            path="/dashboard/*"
                            element={
                                <ProtectedRoute>
                                    <Dashboard />
                                </ProtectedRoute>
                            }
                        />

                        {/* Admin Guide */}
                        <Route
                            path="/admin-guide"
                            element={
                                <AdminRoute>
                                    <AdminGuide />
                                </AdminRoute>
                            }
                        />

                        {/* Debug Route - Available in Development Only */}
                        {import.meta.env.DEV && (
                            <Route
                                path="/database-debug"
                                element={
                                    <AdminRoute>
                                        <DatabaseDebug />
                                    </AdminRoute>
                                }
                            />
                        )}
                        {/* Catch-all 404 Route */}
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </Suspense>
            </Layout>
        </ErrorBoundary>
    );
}

export default App;
